import React, { createContext, useCallback, useContext, useState } from 'react';
import styled, { keyframes } from 'styled-components';


const ICONS = {
  info: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
    </svg>
  ),
  warning: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
      <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
    </svg>
  ),
  success: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12"/>
    </svg>
  ),
  danger: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/>
    </svg>
  ),
};

const DEFAULT_TITLES = {
  info:    'Notice',
  warning: 'Warning',
  success: 'Done',
  danger:  'Are you sure?',
};


const overlayIn = keyframes`from{opacity:0}to{opacity:1}`;
const dialogIn  = keyframes`from{opacity:0;transform:scale(0.96) translateY(6px)}to{opacity:1;transform:scale(1) translateY(0)}`;


const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.42);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 99999;
  padding: 1.5rem;
  animation: ${overlayIn} 0.15s ease;
`;

const Dialog = styled.div`
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 8px 40px rgba(0,0,0,0.18);
  width: 100%;
  max-width: 400px;
  display: flex;
  flex-direction: column;
  animation: ${dialogIn} 0.18s ease;
  overflow: hidden;
  border: 1.5px solid ${p =>
    p.$type === 'warning' ? '#f57c00' :
    p.$type === 'success' ? '#388e3c' :
    p.$type === 'danger'  ? '#d32f2f' :
    '#e97024'};
`;

const Body = styled.div`
  padding: 1.375rem 1.5rem 1.125rem;
  display: flex;
  gap: 0.875rem;
  align-items: flex-start;
`;

const IconWrap = styled.div`
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 1px;
  background: ${p =>
    p.$type === 'warning' ? 'rgba(245,124,0,0.08)'  :
    p.$type === 'success' ? 'rgba(56,142,60,0.08)'  :
    p.$type === 'danger'  ? 'rgba(211,47,47,0.08)'  :
    'rgba(233,112,36,0.08)'};
  color: ${p =>
    p.$type === 'warning' ? '#f57c00' :
    p.$type === 'success' ? '#388e3c' :
    p.$type === 'danger'  ? '#d32f2f' :
    '#e97024'};
`;

const Content = styled.div`flex:1;min-width:0;`;

const Title = styled.div`
  font-family: 'Poppins', 'Garet', sans-serif;
  font-size: 0.9rem;
  font-weight: 700;
  color: #333;
  line-height: 1.3;
  margin-bottom: 0.375rem;
`;

const Message = styled.div`
  font-family: 'Poppins', 'Garet', sans-serif;
  font-size: 0.85rem;
  color: #666;
  line-height: 1.6;
`;

const PromptInput = styled.input`
  width: 100%;
  margin-top: 0.75rem;
  padding: 0.5rem 0.75rem;
  border: 1.5px solid #e0e0e0;
  border-radius: 6px;
  font-family: 'Poppins', 'Garet', sans-serif;
  font-size: 0.85rem;
  color: #333;
  outline: none;
  transition: border-color 0.15s;
  &:focus { border-color: #e97024; box-shadow: 0 0 0 2px rgba(233,112,36,0.15); }
`;

const Footer = styled.div`
  padding: 0.875rem 1.5rem 1.25rem;
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
`;

const BtnPrimary = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.45rem 1.125rem;
  border-radius: 6px;
  font-family: 'Poppins', 'Garet', sans-serif;
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;
  border: none;
  transition: all 0.15s ease;
  white-space: nowrap;
  background: ${p =>
    p.$type === 'success' ? '#388e3c' :
    p.$type === 'danger'  ? '#d32f2f' :
    '#e97024'};
  color: #fff;
  &:hover {
    background: ${p =>
      p.$type === 'success' ? '#2a6b2c' :
      p.$type === 'danger'  ? '#b71c1c' :
      '#d4611a'};
    transform: translateY(-1px);
  }
  &:disabled { opacity: 0.45; cursor: not-allowed; transform: none; }
`;

const BtnSecondary = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.45rem 1.125rem;
  border-radius: 6px;
  font-family: 'Poppins', 'Garet', sans-serif;
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;
  border: 1px solid #e0e0e0;
  background: transparent;
  color: #666;
  transition: all 0.15s ease;
  white-space: nowrap;
  &:hover { background: #f5f5f5; color: #333; }
`;


const AlertDialogContext = createContext(null);


export function AlertDialogProvider({ children }) {
  const [dialog, setDialog] = useState(null);
  const [promptValue, setPromptValue] = useState('');

  const close = useCallback(() => {
    setDialog(null);
    setPromptValue('');
  }, []);

  const showAlert = useCallback((options) => {
    return new Promise((resolve) => {
      setDialog({
        mode: 'alert',
        type: options.type || 'info',
        title: options.title || DEFAULT_TITLES[options.type || 'info'],
        message: options.message || '',
        confirmText: options.confirmText || 'OK',
        onConfirm: () => { close(); resolve(); },
        onCancel:  () => { close(); resolve(); },
      });
    });
  }, [close]);

  const showConfirm = useCallback((options) => {
    return new Promise((resolve) => {
      setDialog({
        mode: 'confirm',
        type: options.type || 'warning',
        title: options.title || DEFAULT_TITLES[options.type || 'warning'],
        message: options.message || '',
        confirmText: options.confirmText || 'Confirm',
        cancelText:  options.cancelText  || 'Cancel',
        onConfirm: () => { close(); resolve(true);  },
        onCancel:  () => { close(); resolve(false); },
      });
    });
  }, [close]);

  const showPrompt = useCallback((options) => {
    return new Promise((resolve) => {
      setPromptValue('');
      setDialog({
        mode: 'prompt',
        type: options.type || 'warning',
        title: options.title || DEFAULT_TITLES[options.type || 'warning'],
        message: options.message || '',
        confirmText: options.confirmText || 'OK',
        cancelText:  options.cancelText  || 'Cancel',
        placeholder: options.placeholder || '',
        onConfirm: (val) => { close(); resolve(val);  },
        onCancel:  ()    => { close(); resolve(null); },
      });
    });
  }, [close]);


  return (
    <AlertDialogContext.Provider value={{ showAlert, showConfirm, showPrompt }}>
      {children}
      {dialog && (
        <Overlay onClick={() => dialog.onCancel()}>
          <Dialog $type={dialog.type} onClick={e => e.stopPropagation()} role="alertdialog" aria-modal="true">
            <Body>
              <IconWrap $type={dialog.type}>
                {ICONS[dialog.type] || ICONS.info}
              </IconWrap>
              <Content>
                <Title>{dialog.title}</Title>
                <Message>{dialog.message}</Message>
                {dialog.mode === 'prompt' && (
                  <PromptInput
                    autoFocus
                    value={promptValue}
                    placeholder={dialog.placeholder || ''}
                    onChange={e => setPromptValue(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === 'Enter') dialog.onConfirm(promptValue);
                      if (e.key === 'Escape') dialog.onCancel();
                    }}
                  />
                )}
              </Content>
            </Body>
            <Footer>
              {(dialog.mode === 'confirm' || dialog.mode === 'prompt') && (
                <BtnSecondary onClick={() => dialog.onCancel()}>
                  {dialog.cancelText}
                </BtnSecondary>
              )}
              <BtnPrimary
                $type={dialog.type}
                onClick={() => dialog.mode === 'prompt'
                  ? dialog.onConfirm(promptValue)
                  : dialog.onConfirm()
                }
              >
                {dialog.confirmText}
              </BtnPrimary>
            </Footer>
          </Dialog>
        </Overlay>
      )}
    </AlertDialogContext.Provider>
  );
}


export function useAlertDialog() {
  const ctx = useContext(AlertDialogContext);
  if (!ctx) throw new Error('useAlertDialog must be used within AlertDialogProvider');
  return ctx;
}